import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Building2,
    ClipboardList,
    Receipt,
    ShieldCheck,
    Users as UsersIcon,
    Wrench,
} from "lucide-react";
import { AdminAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";

function formatINR(minor) {
    const rupees = Math.round((minor || 0) / 100);
    return `₹${rupees.toLocaleString("en-IN")}`;
}

const KPI_DEFS = [
    { key: "total_tenants",        label: "Total tenants",        icon: Building2,    fmt: (v) => v },
    { key: "total_users",          label: "Total users",          icon: UsersIcon,    fmt: (v) => v },
    { key: "active_tenants_last_7d", label: "Active tenants (7d)", icon: ShieldCheck,  fmt: (v) => v },
    { key: "total_jobcards",       label: "Total jobcards",       icon: Wrench,       fmt: (v) => v },
    { key: "total_invoices",       label: "Total invoices",       icon: ClipboardList, fmt: (v) => v },
    { key: "total_revenue_minor",  label: "Total revenue",        icon: Receipt,      fmt: formatINR },
];

const QUICK_LINKS = [
    { to: "/admin/tenants",   label: "Manage all tenants", note: "Browse, create, suspend, impersonate.", testid: "admin-quick-tenants" },
    { to: "/admin/users",     label: "Cross-tenant users", note: "Filter by tenant, role, status.",       testid: "admin-quick-users" },
    { to: "/admin/audit-log", label: "Platform audit log", note: "Every impersonation & tenant op.",      testid: "admin-quick-audit" },
];

export default function AdminDashboardPage() {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        AdminAPI.metrics()
            .then(setMetrics)
            .catch((e) => setErr(e.message || "Failed to load metrics"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <header>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Platform · Super-admin
                </div>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                    Platform overview
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-zinc-500">
                    A live rollup across every tenant on AutoBrix. Use Tenants to drill in or
                    impersonate; every cross-tenant action lands in the audit log.
                </p>
            </header>

            {err && (
                <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
                    {err}
                </Card>
            )}

            <section
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="admin-kpi-grid"
            >
                {KPI_DEFS.map((kpi) => {
                    const v = metrics ? metrics[kpi.key] : null;
                    return (
                        <Card
                            key={kpi.key}
                            data-testid={`admin-kpi-${kpi.key.replace(/_/g, "-")}`}
                            className="flex flex-col gap-3 p-5"
                        >
                            <div className="flex items-center justify-between">
                                <span className="grid h-9 w-9 place-items-center rounded border border-zinc-200 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                                    <kpi.icon className="h-4 w-4" />
                                </span>
                            </div>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    {kpi.label}
                                </div>
                                <div className="mt-1 font-display text-4xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                                    {loading ? "…" : kpi.fmt(v ?? 0)}
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </section>

            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Card className="flex flex-col gap-2 p-5" data-testid="admin-inv-30d">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                        Invoices · last 30 days
                    </div>
                    <div className="font-display text-3xl font-bold tabular-nums">
                        {loading ? "…" : metrics?.invoices_last_30d ?? 0}
                    </div>
                    <div className="text-xs text-zinc-500">
                        Aggregate count across active tenants. Detailed per-tenant view in Tenants list.
                    </div>
                </Card>

                {QUICK_LINKS.map((q) => (
                    <Link key={q.to} to={q.to} className="group">
                        <Card
                            data-testid={q.testid}
                            className="flex h-full flex-col gap-3 p-5 transition-shadow group-hover:border-brand-red/50 group-hover:shadow-md"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    Quick action
                                </span>
                                <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-red" />
                            </div>
                            <div>
                                <div className="font-display text-lg font-bold tracking-tight">
                                    {q.label}
                                </div>
                                <div className="mt-1 text-xs text-zinc-500">{q.note}</div>
                            </div>
                        </Card>
                    </Link>
                ))}
            </section>
        </div>
    );
}
